import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../config/axios';
import { authService } from '../services/authService';

interface PlanDetails {
  id: number;
  name: string;
  description: string;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  price: number;
  currency: string;
  interval: string;
  daily_recording_limit: number | null;
  total_recording_limit: number | null;
  max_recording_duration: number | null;
  is_active: boolean;
  features: Record<string, any>;
}

interface SubscriptionDetails {
  id: number;
  plan: PlanDetails;
  status: string;
  start_date: string;
  end_date: string | null;
  next_renewal_date?: string | null;
  is_trial: boolean;
  cancel_at_period_end: boolean;
  trial_end?: string | null;
  trial_remaining_days?: number;
}

export const useSubscription = () => {
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [profileSubscriptionStatus, setProfileSubscriptionStatus] = useState<string | null>(null);
  
  // Use a ref to track if we're already fetching
  const isFetchingRef = useRef(false);
  // Cache refresh interval in milliseconds (5 minutes)
  const REFRESH_INTERVAL = 5 * 60 * 1000;
  // Minimum time between manual refreshes to prevent abuse (10 seconds)
  const MANUAL_REFRESH_COOLDOWN = 10 * 1000;
  
  // Fetch user profile for subscription status as a fallback
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = authService.getToken();
        if (!token) return;
        
        const response = await axios.get('/api/scribing/user-profile/me');
        if (response.status === 200 && response.data) {
          if (response.data.subscription_status) {
            setProfileSubscriptionStatus(response.data.subscription_status);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    fetchUserProfile();
  }, []);
  
  const fetchSubscriptionDetails = useCallback(async (force = false) => {
    // Skip if already fetching
    if (isFetchingRef.current) {
      // Already fetching, skip
      return;
    }
    
    // Check if we should skip the fetch due to recent fetch unless forcing
    const now = Date.now();
    if (!force && now - lastFetchTime < MANUAL_REFRESH_COOLDOWN) {
      // Recent fetch, skip
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      
      const token = authService.getToken();
      
      if (!token) {
        // No auth token
        setSubscriptionDetails(null);
        return;
      }
      
      const response = await axios.get('/api/scribing/subscriptions/current', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setSubscriptionDetails(response.data);
      setLastFetchTime(now);
      
      // Save to localStorage for backup/caching
      localStorage.setItem('subscription_details', JSON.stringify(response.data));
    } catch (error: any) {
      console.error('Error fetching subscription data:', error);
      
      // If API call fails, try to use cached data from localStorage
      try {
        const cachedData = localStorage.getItem('subscription_details');
        if (cachedData) {
          // Using cached data
          setSubscriptionDetails(JSON.parse(cachedData));
        } else {
          setSubscriptionDetails(null);
        }
      } catch (e) {
        console.error('Error parsing cached subscription data:', e);
        setSubscriptionDetails(null);
      }
      
      setError(error.message || 'Failed to fetch subscription details');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [lastFetchTime]);
  
  // Only set up the effect once and don't include fetchSubscriptionDetails in deps
  useEffect(() => {
    // Initial fetch
    fetchSubscriptionDetails(true);
    
    // Set up periodic refresh - fetch every 5 minutes
    const refreshInterval = setInterval(() => {
      // Scheduled refresh
      fetchSubscriptionDetails(true);
    }, REFRESH_INTERVAL);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, []); // Empty dependency array to run only once
  
  // Expose a memoized refresh function that will force a refresh
  const refreshSubscription = useCallback(() => {
    // Manual refresh
    return fetchSubscriptionDetails(true);
  }, [fetchSubscriptionDetails]);
  
  const isTrial = subscriptionDetails?.is_trial || false;
  // Check both sources for free status
  const isFree = subscriptionDetails?.status?.toLowerCase() === 'free' || profileSubscriptionStatus?.toLowerCase() === 'free';
  const isPaid = (subscriptionDetails?.status?.toLowerCase() === 'active' && !isTrial && !isFree);
  const planName = subscriptionDetails?.plan?.name || '';
  const trialEnd = subscriptionDetails?.trial_end || subscriptionDetails?.end_date || null;
  
  // Check if subscription is expired
  const isExpired = subscriptionDetails?.status?.toLowerCase() === 'expired';
  
  // Calculate remaining trial days from the end_date
  const calculateTrialDaysRemaining = () => {
    // Always calculate based on actual dates for accuracy
    if (!trialEnd) return 0;
    
    const endDate = new Date(trialEnd);
    const now = new Date();
    
    // Calculate the time difference in milliseconds
    const timeDiff = endDate.getTime() - now.getTime();
    
    // Convert milliseconds to days (rounded up so it shows 1 day until the very last day)
    const daysRemaining = Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
    
    return daysRemaining;
  };
  
  const trialDaysRemaining = calculateTrialDaysRemaining();
  const status = subscriptionDetails?.status || profileSubscriptionStatus || '';
  const isCanceled = subscriptionDetails?.status?.toLowerCase() === 'canceled';
  
  // Treat 'free', 'expired', and 'canceled' status as special states that should show the banner
  const shouldShowBanner = isTrial || isFree || isCanceled || isExpired;
  
  return {
    isTrial,
    isFree,
    isPaid,
    planName,
    trialEnd,
    trialDaysRemaining,
    status,
    isCanceled,
    isExpired,
    shouldShowBanner,
    isLoading,
    error,
    subscriptionDetails,
    refreshSubscription
  };
}; 